/**
 * Per-card SEO permalink page — /tarot/card/:cardId
 * Public, no auth, deeply SEO-optimized for "{card name} tarot meaning" queries.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Sparkles, Share2, MessageCircle, Facebook, Twitter, Send, Link2 } from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import SponsoredTools from '../../components/SponsoredTools';
import { cardRoman } from './tarotRoman';

const SITE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const TarotCardDetail = () => {
  const { cardId } = useParams();
  const { user } = useAuth();
  const [card, setCard] = useState(null);
  const [allCards, setAllCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // Track referral click when landing here via a shared card link. Also persist
    // in localStorage so the ref survives navigation to /register.
    try {
      const refCode = new URL(window.location.href).searchParams.get('ref');
      if (refCode && refCode.trim()) {
        const clean = refCode.trim();
        try { localStorage.setItem('anant_ref', clean); } catch (_) { /* storage disabled */ }
        api.post(`/referral/track-click/${encodeURIComponent(clean)}`).catch(() => {});
      }
    } catch (_) { /* url parse failed */ }
    api.get('/anant-tarot/public-cards')
      .then(r => {
        const cards = r.data.cards || [];
        setAllCards(cards);
        const c = cards.find(x => String(x.cardId) === String(cardId));
        setCard(c || null);
        setLoading(false);
        // ===== Inject rich SEO meta tags =====
        if (c) {
          const title = `${c.name} — Tarot Card Meaning & Spiritual Reading | Anant Inner Self`;
          const desc = (c.meaning || `Explore the deep meaning of the ${c.name} tarot card from the Osho-Zen tradition. Free reading, interpretation and personal guidance available.`).slice(0, 220);
          document.title = title;
          const setMeta = (sel, attr, val) => {
            let tag = document.querySelector(sel);
            if (!tag) { tag = document.createElement('meta'); tag.setAttribute(attr.split('=')[0], attr.split('=')[1].replace(/"/g, '')); document.head.appendChild(tag); }
            tag.setAttribute('content', val);
          };
          const ensure = (key, val) => {
            let tag = document.querySelector(`meta[name="${key}"]`);
            if (!tag) { tag = document.createElement('meta'); tag.setAttribute('name', key); document.head.appendChild(tag); }
            tag.setAttribute('content', val);
          };
          const ensureOg = (prop, val) => {
            let tag = document.querySelector(`meta[property="${prop}"]`);
            if (!tag) { tag = document.createElement('meta'); tag.setAttribute('property', prop); document.head.appendChild(tag); }
            tag.setAttribute('content', val);
          };
          ensure('description', desc);
          ensure('keywords', `${c.name} tarot, ${c.name} meaning, osho zen ${c.name}, ${c.suit} tarot, hindi tarot, spiritual card meaning, anant tarot, free tarot reading`);
          ensureOg('og:title', title);
          ensureOg('og:description', desc);
          ensureOg('og:type', 'article');
          ensureOg('og:url', window.location.href);
          ensureOg('og:image', `${SITE}${c.image_url}`);
          ensureOg('og:image:width', '800');
          ensureOg('og:image:height', '1200');
          ensure('twitter:card', 'summary_large_image');
          ensure('twitter:title', title);
          ensure('twitter:description', desc);
          ensure('twitter:image', `${SITE}${c.image_url}`);

          // JSON-LD Structured Data for Google rich result
          const ld = document.getElementById('tarot-card-ld') || document.createElement('script');
          ld.type = 'application/ld+json';
          ld.id = 'tarot-card-ld';
          ld.text = JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            'headline': title,
            'description': desc,
            'image': `${SITE}${c.image_url}`,
            'author': { '@type': 'Organization', 'name': 'Anant Web Portal' },
            'publisher': { '@type': 'Organization', 'name': 'Anant Web Portal' },
            'mainEntityOfPage': window.location.href,
          });
          document.head.appendChild(ld);
        }
      })
      .catch(() => setLoading(false));
  }, [cardId]);

  if (loading) return <div className="min-h-screen pt-24 text-center text-slate-400 bg-slate-950">Loading…</div>;
  if (!card) {
    return (
      <div className="min-h-screen pt-24 px-4 bg-slate-950 text-center">
        <div className="text-amber-300 text-2xl font-bold">Card not found</div>
        <Link to="/tarot/gallery" className="text-amber-200 underline mt-4 inline-block">← Back to Gallery</Link>
      </div>
    );
  }

  // Find prev / next for navigation
  const idx = allCards.findIndex(x => String(x.cardId) === String(cardId));
  const prev = idx > 0 ? allCards[idx - 1] : null;
  const next = idx < allCards.length - 1 ? allCards[idx + 1] : null;

  // Build share URLs. IMPORTANT: use backend /card-og/ endpoint so social scrapers
  // (WhatsApp, Facebook, X, Telegram) receive the correct card artwork as og:image
  // instead of the site's default logo. The endpoint auto-redirects real users to
  // this card detail page. Referral code auto-appended for all 4 socials so every
  // share fuels the viral loop.
  const url = window.location.href;
  const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const referralCode = user?.username || user?.referral_code || '';
  const refQuery = referralCode ? `?ref=${encodeURIComponent(referralCode)}` : '';
  const ogShareUrl = `${backendBase}/api/anant-tarot/card-og/${card.cardId}${refQuery}`;
  const shareTitle = `🔮 ${card.name} — Anant Inner Self Tarot`;
  const shareBody = (card.meaning || card.basic_meaning || 'A spiritual card from the Anant Zen Tarot deck').slice(0, 180);
  const shareText = `${shareTitle}\n\n${shareBody}\n\nRead full → ${ogShareUrl}\n\nGet your personal reading: ${window.location.origin}/anant-tarot`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const tgUrl = `https://t.me/share/url?url=${encodeURIComponent(ogShareUrl)}&text=${encodeURIComponent(shareTitle + ' — ' + shareBody)}`;
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogShareUrl)}&quote=${encodeURIComponent(shareTitle)}`;
  const twUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle + ' — ' + shareBody)}&url=${encodeURIComponent(ogShareUrl)}`;

  return (
    <div className="min-h-screen pt-20 sm:pt-24 pb-16 px-4 sm:px-6 bg-slate-950 text-white" data-testid="tarot-card-detail-page">
      <div className="max-w-5xl mx-auto">
        {/* Breadcrumb */}
        <nav className="text-xs text-slate-400 mb-5 flex items-center gap-2 flex-wrap" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-amber-300">Home</Link>
          <span>/</span>
          <Link to="/tarot/gallery" className="hover:text-amber-300">Tarot Gallery</Link>
          <span>/</span>
          <span className="text-amber-200">{card.suit}</span>
          <span>/</span>
          <span className="text-amber-300 font-semibold">{card.name}</span>
        </nav>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {/* Card image */}
          <div>
            <div className="rounded-2xl overflow-hidden bg-slate-900 border border-amber-500/30 shadow-xl">
              <img
                src={`${SITE}${card.image_url}`}
                alt={`${card.name} — ${card.suit} tarot card from Anant Inner Self`}
                className="w-full h-auto object-cover"
                style={{ filter: 'saturate(1.25) contrast(1.1) brightness(1.05) drop-shadow(0 0 12px rgba(255,200,80,0.3))' }}
                data-testid="card-hero-image"
              />
            </div>
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2 font-semibold">Share this card</div>
              <div className="grid grid-cols-5 gap-2">
                <a href={waUrl} target="_blank" rel="noreferrer" data-testid="card-share-whatsapp"
                   title="Share on WhatsApp"
                   className="flex items-center justify-center py-2.5 rounded-xl bg-green-600 text-white hover:bg-green-500 transition-transform hover:scale-105">
                  <MessageCircle className="w-4 h-4" />
                </a>
                <a href={fbUrl} target="_blank" rel="noreferrer" data-testid="card-share-facebook"
                   title="Share on Facebook"
                   className="flex items-center justify-center py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-500 transition-transform hover:scale-105">
                  <Facebook className="w-4 h-4" />
                </a>
                <a href={twUrl} target="_blank" rel="noreferrer" data-testid="card-share-twitter"
                   title="Share on X (Twitter)"
                   className="flex items-center justify-center py-2.5 rounded-xl bg-slate-900 text-white border border-slate-600 hover:bg-slate-800 transition-transform hover:scale-105">
                  <Twitter className="w-4 h-4" />
                </a>
                <a href={tgUrl} target="_blank" rel="noreferrer" data-testid="card-share-telegram"
                   title="Share on Telegram"
                   className="flex items-center justify-center py-2.5 rounded-xl bg-sky-500 text-white hover:bg-sky-400 transition-transform hover:scale-105">
                  <Send className="w-4 h-4" />
                </a>
                <button
                  data-testid="card-copy-link"
                  onClick={() => { try { navigator.clipboard.writeText(ogShareUrl); } catch (_) { /* clipboard not available */ } }}
                  title="Copy link"
                  className="flex items-center justify-center py-2.5 rounded-xl bg-amber-500 text-black hover:bg-amber-400 transition-transform hover:scale-105"
                ><Link2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>

          {/* Card content */}
          <article>
            <div className="text-xs text-slate-400 uppercase tracking-[0.3em] mb-2">{card.suit}</div>
            <h1 className="text-4xl sm:text-5xl font-extrabold bg-gradient-to-r from-amber-300 via-orange-300 to-rose-300 bg-clip-text text-transparent leading-tight mb-1" data-testid="card-name-heading">
              {card.name}
            </h1>
            {cardRoman(card) && (
              <div className="text-lg font-serif italic tracking-[0.35em] text-amber-400/90 mb-3" data-testid="card-roman">{cardRoman(card)}</div>
            )}
            {card.keywords && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {card.keywords.split(/[,;|]/).slice(0, 6).map((k, i) => (
                  <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/15 text-amber-200 border border-amber-500/30">
                    {k.trim()}
                  </span>
                ))}
              </div>
            )}
            <div className="prose prose-invert max-w-none">
              <h2 className="text-xl font-bold text-amber-200 mt-0">What this card means</h2>
              <p className="text-slate-200 leading-relaxed text-base">
                {card.meaning || `The ${card.name} card invites you to pause and listen to your inner voice. Each draw of this card whispers a unique message — meaningful only to the heart that asks the right question.`}
              </p>

              <h2 className="text-xl font-bold text-amber-200 mt-6">When this card appears</h2>
              <p className="text-slate-200 leading-relaxed">
                When the <strong>{card.name}</strong> shows up in your spread, the universe is highlighting a chapter of your life that requires gentle attention.
                It speaks of transformation, surrender, and the courage to honor what is true for you in this moment.
                The {card.suit.toLowerCase()} resonance reminds us that even the smallest insight, fully received, can change the entire direction of a life.
              </p>

              <h2 className="text-xl font-bold text-amber-200 mt-6">Get a personal 5-card reading</h2>
              <p className="text-slate-200 leading-relaxed">
                Ready to ask the universe a question of your own? Our AI-guided 5-card spread combines the {card.name} with four other cards drawn just for you, then weaves them into a complete emotional reading in your chosen language (Hindi, English, Marathi, Tamil & more).
              </p>
              <Link
                to="/anant-tarot"
                data-testid="card-get-reading-cta"
                className="not-prose mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-amber-500 text-black font-bold hover:bg-amber-400"
              >
                <Sparkles className="w-4 h-4" /> Draw Your 5-Card Spread
              </Link>
            </div>
          </article>
        </div>

        {/* Sponsored Spiritual Tools widget */}
        <SponsoredTools context="card-detail" maxItems={4} />

        {/* Prev / Next nav */}
        <div className="mt-12 grid grid-cols-2 gap-3">
          {prev ? (
            <Link to={`/tarot/card/${prev.cardId}`} data-testid="card-prev"
              className="rounded-xl p-4 bg-slate-900 border border-slate-800 hover:border-amber-400 flex items-center gap-3 group">
              <ArrowLeft className="w-4 h-4 text-amber-300" />
              <div>
                <div className="text-[10px] text-slate-500 uppercase">Previous</div>
                <div className="font-bold text-amber-200 group-hover:text-amber-100 truncate">{prev.name}</div>
              </div>
            </Link>
          ) : <div />}
          {next ? (
            <Link to={`/tarot/card/${next.cardId}`} data-testid="card-next"
              className="rounded-xl p-4 bg-slate-900 border border-slate-800 hover:border-amber-400 flex items-center gap-3 justify-end text-right group">
              <div>
                <div className="text-[10px] text-slate-500 uppercase">Next</div>
                <div className="font-bold text-amber-200 group-hover:text-amber-100 truncate">{next.name}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-amber-300" />
            </Link>
          ) : <div />}
        </div>

        {/* Back to gallery */}
        <div className="mt-8 text-center">
          <Link to="/tarot/gallery" data-testid="card-back-gallery" className="text-amber-300 underline text-sm">← Browse all 79 cards in the Tarot Gallery</Link>
        </div>
      </div>
    </div>
  );
};

export default TarotCardDetail;
